<script setup lang="ts">
import { computed } from 'vue'

import type { ReportColumn } from '@/features/reports'
import { useFormat } from '@/shared/lib/format'
import { Icon, Select, type IconName, type SelectOption } from '@/shared/ui'

const props = defineProps<{
  metric: ReportColumn
  options: SelectOption<string>[]
  active: boolean
  color: string
  currentTotal: number
  previousTotal: number
  formattedValue: string
  removeLabel: string
}>()

const emit = defineEmits<{
  toggle: []
  remove: []
  change: [field: string]
}>()
const format = useFormat()
const selected = computed({
  get: () => props.metric.field,
  set: (field: string | null) => {
    if (field) emit('change', field)
  },
})
const change = computed(() =>
  props.previousTotal === 0
    ? null
    : ((props.currentTotal - props.previousTotal) / props.previousTotal) * 100,
)
const tone = computed(() => {
  if (change.value === null || change.value === 0) return 'neutral'
  const positive = change.value > 0
  // A metric without a catalog `change_type` still gets colored: default to the
  // "higher is better" direction rather than leaving it gray.
  return props.metric.change_type === 'negative'
    ? positive
      ? 'danger'
      : 'success'
    : positive
      ? 'success'
      : 'danger'
})
const formattedChange = computed(() =>
  change.value === null
    ? ''
    : format.formatPercent(change.value, {
        scale: 'percent',
        minFrac: 2,
        maxFrac: 2,
        signDisplay: 'always',
      }),
)
// Keep the legacy v3 metric semantics while routing every vendor glyph through the shared Icon.
const FIELD_ICON: Partial<Record<string, IconName>> = {
  cost: 'coins',
  purchase: 'sack-dollar',
  conversions: 'chart-line-up',
  sessions: 'traffic-cone',
  view_cart: 'bookmark',
  add_to_cart: 'cart-shopping',
  page_view: 'browser',
  first_visit: 'eye',
  add_to_wishlist: 'heart',
  app_remove: 'trash',
  first_open: 'door-open',
  login: 'right-to-bracket',
  click: 'arrow-pointer',
  search: 'arrow-pointer',
  select_item: 'hand',
  roas: 'chart-mixed',
  revenue: 'money-bill',
  purchase_revenue: 'money-bill',
}

function getIcon(metric: ReportColumn): IconName {
  return metric.icon ?? FIELD_ICON[metric.field] ?? 'database'
}
</script>

<template>
  <div
    class="summary-overview-metric flex min-w-44 max-w-60 flex-none cursor-pointer items-center gap-xs rounded-md border shadow-sm bg-surface p-1.5 text-primary opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
    :class="[
      active ? 'is-active border-[var(--summary-metric-color)] opacity-100' : 'border-transparent',
      `is-${tone}`,
    ]"
    :style="{ '--summary-metric-color': color }"
    role="button"
    tabindex="0"
    @click="emit('toggle')"
    @keydown.enter.prevent="emit('toggle')"
    @keydown.space.prevent="emit('toggle')"
  >
    <span
      class="summary-overview-metric__icon grid h-10 w-10 place-items-center rounded-md bg-[var(--summary-metric-color)] text-primary-fg"
      aria-hidden="true"
    >
      <Icon :name="getIcon(metric)" :size="20" />
    </span>
    <div class="summary-overview-metric__body min-w-0 flex-1">
      <div class="summary-overview-metric__top flex items-center gap-2xs" @click.stop>
        <div
          class="summary-overview-metric__select min-w-0 flex-1 text-sm font-semibold leading-5"
          :title="metric.header"
        >
          <Select
            v-model="selected"
            :options="options"
            variant="picker"
            searchable
            size="sm"
            panel-width="14rem"
          />
        </div>
        <button
          type="button"
          class="summary-overview-metric__remove inline-flex flex-none cursor-pointer border-0 bg-transparent p-0 text-muted"
          :aria-label="removeLabel"
          @click="emit('remove')"
        >
          <Icon name="x" :size="13" />
        </button>
      </div>
      <div class="summary-overview-metric__value flex items-end gap-2xs font-sans">
        <strong>{{ formattedValue }}</strong>
        <span
          v-if="change !== null"
          class="summary-overview-metric__change text-sm font-bold leading-5"
          :class="{
            'text-success': tone === 'success',
            'text-danger': tone === 'danger',
            'text-primary-soft-text': tone === 'neutral',
          }"
        >
          {{ formattedChange }}
        </span>
      </div>
    </div>
  </div>
</template>
